import {Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch} from "@stencil/core"
import {createID} from "../../../utils/string.utils"
import {validateProp} from "../../../helpers/Validate/props"
import {FormSwitchClass, FormSwitchSizes, FormSwitchSizesType} from "./constants"
import {govHost, toBoolAttr, toBoolAttrIfDefined} from "../../../helpers/Dom/template"
import {GovForm} from "../../../helpers/Dom/form.types"
import {GovHost} from "../../../helpers/Dom/template.types"
import {govForm} from "../../../helpers/Dom/form"
import Fragment from "../../../helpers/Fragment"
import {FormSwitchEvent} from "./gov-form-switch.types"
import {AttributeObserver, Attributes} from "../../../helpers/Dom/attribute";
import {ariaAttributes} from "../../../helpers/Accessibility/attributes";
import {inheritAriaAttributes} from "../../../helpers/Accessibility/accessibility";

@Component({
	tag: "gov-form-switch",
})
export class GovFormSwitch {
	private readonly h: GovHost
	private f: GovForm
	private inputRef?: HTMLInputElement
	private attributeObserver: AttributeObserver

	private readonly checkboxId: string
	private hydrated = false

	constructor() {
		this.checkboxId = createID("GovCheckbox")

		this.h = govHost(this.host)
		this.f = govForm(this.h)
	}

	@Element() host: HTMLGovFormSwitchElement
	/**
	 * Value of switch
	 */
	@Prop() readonly value: string
	/**
	 * When you can't use the form label.
	 */
	@Prop({attribute: "no-label"}) readonly noLabel: boolean = false
	/**
	 * Switch button state
	 */
	@Prop({reflect: true, mutable: true}) checked = false
	/**
	 * Set whether the input is required or not. Please note that this is necessary for accessible inputs when the user is required to fill them.
	 */
	@Prop() readonly required: boolean = false
	/**
	 * Makes the switch component disabled.
	 */
	@Prop({reflect: true}) readonly disabled: boolean = false
	/**
	 * Name of the switch.
	 */
	@Prop() readonly name: string
	/**
	 * Switch's size.
	 */
	@Prop({reflect: true}) readonly size?: FormSwitchSizesType = "m"
	/**
	 * Custom switch identifier.
	 */
	@Prop({attribute: "identifier"}) readonly identifier: string
	/**
	 * Indicates the entered value does not conform to the format expected by the application.
	 */
	@Prop() readonly invalid: boolean
	/**
	 * Emitted when the switch has focus.
	 */
	@Event({eventName: "gov-focus"}) govFocus: EventEmitter<FormSwitchEvent>
	/**
	 * Emitted when the switch loses focus.
	 */
	@Event({eventName: "gov-blur"}) govBlur: EventEmitter<FormSwitchEvent>
	/**
	 * Emitted when the switch change value.
	 */
	@Event({eventName: "gov-change"}) govChange: EventEmitter<FormSwitchEvent>

	@Watch("size")
	validateSize(newValue: string): void {
		validateProp(FormSwitchSizes, newValue, FormSwitchClass.root)
	}

	@Watch("disabled")
	@Watch("invalid")
	@Watch("size")
	watchDisabled(): void {
		this.passControlAttrs()
	}

	@State() inheritedAttributes: Attributes = {}

	private passControlAttrs() {
		this.f.passAttrToControl("size", this.size)
		this.f.passAttrToControl("invalid", toBoolAttr(this.invalid))
		this.f.passAttrToControl("disabled", toBoolAttr(this.disabled))
		this.f.passAttrToControl("type", "switch")
	}

	componentDidLoad() {
		this.attributeObserver = new AttributeObserver(this.host, (attributeName, newValue) => {
			this.onAriaAttributeChange(attributeName, newValue);
		});

		this.attributeObserver.startObserving(ariaAttributes);
	}

	componentWillLoad() {
		this.passControlAttrs()
		this.inheritedAttributes = inheritAriaAttributes(this.host);
	}

	disconnectedCallback() {
		this.attributeObserver?.stopObserving();
	}

	async componentDidRender() {
		this.f.passAttrToLabel("identifier", this.identifier || this.checkboxId)
		this.f.passAttrToLabel("required", String(this.required))
		this.passControlAttrs()
	}

	connectedCallback() {
		this.hydrated = true
	}

	onAriaAttributeChange(attributeName: string, newValue: string) {
		this.host.removeAttribute(attributeName)
		this.inheritedAttributes = {...this.inheritedAttributes, [attributeName]: newValue};
	}

	render() {
		const wrapSwitch = (children) => {
			if (this.noLabel) {
				return (
					<label class={FormSwitchClass.label}>
						{children}
					</label>
				)
			}
			return children
		}

		const noLabelSlot = !this.host.querySelector('[slot="label"]');
		const hostClasses = `${this.h.classes(FormSwitchClass.root)}${noLabelSlot ? ` ${FormSwitchClass.noLabel}` : ''}`;

		return (
			<Host class={hostClasses} hydrated={this.hydrated} checked={this.checked}
				  size={this.size} invalid={toBoolAttrIfDefined(this.invalid)}>
				{wrapSwitch(
					<Fragment>
						<input
							class={FormSwitchClass.input}
							id={this.identifier || this.checkboxId}
							type="checkbox"
							value={this.value}
							name={this.name}
							disabled={this.disabled}
							checked={this.checked}
							onClick={this.onClickHandler.bind(this)}
							onChange={this.onChangeHandler.bind(this)}
							onFocus={this.onFocusHandler.bind(this)}
							onBlur={this.onBlurHandler.bind(this)}
							ref={el => (this.inputRef = el as HTMLInputElement)}
							required={this.required}
							{...this.inheritedAttributes}
							aria-checked={this.checked}
							aria-required={toBoolAttrIfDefined(this.required)}
							aria-invalid={toBoolAttrIfDefined(this.invalid)}
							aria-disabled={toBoolAttrIfDefined(this.disabled)}
						/>
						<span>
							<span class={FormSwitchClass.indicator}>
								{this.checked && <gov-icon name="check-lg" type="components"></gov-icon>}
							</span>
							<slot name="label"></slot>
						</span>
					</Fragment>,
				)}
			</Host>
		)
	}

	private onClickHandler(e: Event) {
		e.stopPropagation()
	}

	private onFocusHandler(e: FocusEvent) {
		e.stopPropagation()
		this.govFocus.emit({
			component: FormSwitchClass.root,
			checked: this.checked,
			value: this.value,
			originalEvent: e,
		})
	}

	private onBlurHandler(e: FocusEvent) {
		e.stopPropagation()
		this.govBlur.emit({
			component: FormSwitchClass.root,
			checked: this.checked,
			value: this.value,
			originalEvent: e,
		})
	}

	private onChangeHandler(e: Event) {
		this.checked = (e.target as HTMLInputElement).checked
		e.stopPropagation()
		this.govChange.emit({
			component: FormSwitchClass.root,
			checked: this.checked,
			value: this.value,
			originalEvent: e,
		})
	}

	/**
	 * Returns the reference of the native element
	 */
	@Method()
	async getRef(): Promise<HTMLInputElement> {
		return this.inputRef
	}
}
